import React, { useState, useEffect } from 'react';
import { MODULES, ACTIONS } from '../context/PermissionsContext';
import api from '../services/api';

const HierarchicalPermissionsManager = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [selectedModule, setSelectedModule] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const rolesData = await api.roles.getAll();
      setRoles(rolesData);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoles([]);
    }
  };

  const loadRolePermissions = async (roleId) => {
    try {
      setLoading(true);
      const response = await api.roles.getPermissions(roleId);
      if (response && response.permissions) {
        setRolePermissions(response.permissions);
      } else {
        setRolePermissions({});
      }
    } catch (error) {
      console.log('No permissions found for role');
      setRolePermissions({});
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;
    
    try {
      setLoading(true);
      await api.roles.create({ name: newRoleName, permissions: {} });
      setNewRoleName('');
      loadRoles();
      setMessage({ text: 'Role created successfully!', type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: 'Error creating role: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setSelectedModule('');
    loadRolePermissions(role.id);
  };

  const handlePermissionToggle = (module, submodule, action) => {
    setRolePermissions(prev => {
      const modulePerms = prev[module] || {};
      const submodulePerms = modulePerms[submodule] || [];
      const hasPermission = submodulePerms.includes(action);
      
      return {
        ...prev,
        [module]: {
          ...modulePerms,
          [submodule]: hasPermission 
            ? submodulePerms.filter(a => a !== action)
            : [...submodulePerms, action]
        }
      };
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    
    try {
      setLoading(true);
      await api.roles.updatePermissions(selectedRole.id, { permissions: rolePermissions });
      setMessage({ text: 'Permissions updated successfully!', type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: 'Error updating permissions: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getModuleDisplayName = (moduleKey) => {
    return moduleKey.replace('_', ' ').toUpperCase();
  };

  const getSubmoduleDisplayName = (submoduleKey) => {
    return submoduleKey.replace(/_/g, ' ').toUpperCase();
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Role Hierarchical Permissions</h5>
      </div>
      <div className="card-body">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'}`}>
            {message.text}
          </div>
        )}

        <div className="row mb-4">
          <div className="col-md-3">
            <label className="form-label">Create New Role</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Role name"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
              />
              <button className="btn btn-success" onClick={handleCreateRole} disabled={loading}>
                Create
              </button>
            </div>
          </div>
          <div className="col-md-3">
            <label className="form-label">Select Role</label>
            <select 
              className="form-control"
              onChange={(e) => {
                const role = roles.find(r => r.id === parseInt(e.target.value));
                if (role) handleRoleSelect(role);
              }}
              value={selectedRole?.id || ''}
            >
              <option value="">Choose a role...</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Select Module</label>
            <select 
              className="form-control"
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              disabled={!selectedRole}
            >
              <option value="">Choose a module...</option>
              {Object.keys(MODULES).map(moduleKey => (
                <option key={moduleKey} value={moduleKey}>
                  {getModuleDisplayName(moduleKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6 d-flex align-items-end">
            <button 
              className="btn btn-primary"
              onClick={handleSavePermissions}
              disabled={!selectedRole || loading}
            >
              {loading ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </div>

        {selectedRole && selectedModule && (
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                {getModuleDisplayName(selectedModule)} - Submodule Permissions
              </h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>Submodule</th>
                      {Object.values(ACTIONS).map(action => (
                        <th key={action} className="text-center">
                          {action.toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(MODULES[selectedModule].submodules).map(([submoduleKey, submoduleValue]) => (
                      <tr key={submoduleKey}>
                        <td className="fw-bold">
                          {getSubmoduleDisplayName(submoduleValue)}
                        </td>
                        {Object.values(ACTIONS).map(action => (
                          <td key={action} className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={
                                rolePermissions[MODULES[selectedModule].name]?.[submoduleValue]?.includes(action) || false
                              }
                              onChange={() => handlePermissionToggle(
                                MODULES[selectedModule].name, 
                                submoduleValue, 
                                action
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {selectedRole && !selectedModule && (
          <div className="alert alert-info">
            <strong>Select a module</strong> to configure submodule permissions for {selectedRole.name}
          </div>
        )}

        {!selectedRole && (
          <div className="text-center text-muted py-4">
            <p>Select a role to manage hierarchical permissions</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HierarchicalPermissionsManager;